// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for creating new assets in the system.
 * Provides functionality to register a new asset with its initial location,
 * serial number, and inventory date.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetSchema } from '@/asset/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when creating a new asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const createAssetResponseSchema = z
  .object({
    data: assetSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

type CreateAssetResponse = z.infer<typeof createAssetResponseSchema>;

/**
 * Parameters required to create a new asset.
 * @typedef {Object} CreateAssetParameters
 * @property {string} itemId - Unique identifier for the item of the asset
 * @property {string} serial - Unique serial number for the asset
 * @property {string} locationId - Initial location identifier for the asset
 * @property {DateTime} inventoryDate - Date when the asset was added to inventory
 */
interface CreateAssetParameters {
  itemId: string;
  serial: string;
  locationId: string;
  inventoryDate: DateTime;
}

/**
 * Creates a new asset in the system with the specified parameters.
 * This function handles the API call to register a new asset and validates
 * the response data using Zod schemas.
 *
 * @param {CreateAssetParameters} parameters - Required parameters for asset creation
 * @property {string} parameters.itemId - The ID of the asset's item
 * @property {string} parameters.serial - The unique serial number for the asset
 * @property {string} parameters.locationId - The ID of the asset's initial location
 * @property {DateTime} parameters.inventoryDate - The date the asset was added to inventory
 * @returns {Promise<CreateAssetResponse>} The created asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function createAsset(
  parameters: CreateAssetParameters
): Promise<CreateAssetResponse> {
  const url = '/v1/asset';
  const response = await api.post(url, {
    itemId: parameters.itemId,
    serial: parameters.serial,
    locationId: parameters.locationId,
    inInventoryDate: serializeDateForAPI(parameters.inventoryDate),
  });

  return parseResponse(createAssetResponseSchema, response.data, {
    schemaName: 'CreateAsset',
    endpoint: url,
  });
}
